import Vector from "victor";
import Rect from "./types/Rect";
import Camera from "./Camera";
import { ShapeWithCanvasBasedDOMRepresentation2, Shape2 } from "./types/Shape";
import { EraserShape2 } from "./pen/PenShape";
import { shapeTruePosition2, shapeBoundingRect2, shapeScale2 } from "./util";
import { PenShapeRenderer } from "./pen/PenShapeRenderer";
import { AttributeKey, PartialUnknownShape } from "./types/attributes";

export default abstract class CanvasBasedDOMRepresentation {
  id: string;
  shape: ShapeWithCanvasBasedDOMRepresentation2;
  root: HTMLDivElement;
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D | null = null;
  camera: Camera;
  eraserRenderers = new Array<PenShapeRenderer>();

  lastRenderedShape: ShapeWithCanvasBasedDOMRepresentation2 | null = null;
  isOnscreen = true;

  constructor(
    id: string,
    shape: Shape2,
    transformEl: HTMLElement,
    camera: Camera
  ) {
    this.root = document.createElement("div");
    this.root.dataset.subjectID = id;
    this.root.className = "WBNodeRoot";
    // this.root.dataset.subjectID = id;
    this.canvas = document.createElement("canvas");
    this.canvas.className = "WBBoundingRectCanvas";
    this.root.appendChild(this.canvas);

    this.camera = camera;
    this.shape = shape as ShapeWithCanvasBasedDOMRepresentation2;
    this.id = id;
    // this.el.dataset.shape = JSON.stringify(shape);
    // this.el.dataset.shapedata = JSON.stringify(data);
    this.ctx = this.canvas.getContext("2d");

    transformEl.appendChild(this.root);
    this.prepareCanvasForDrawing(this.getBackingScale(), this.canvas, true);
  }

  get maySelect(): boolean {
    return true;
  }

  get boundingRect(): Rect {
    return shapeBoundingRect2(this.shape);
  }

  get selectionRectInset(): Vector {
    return new Vector(0, 0);
  }

  get selectionRect(): Rect {
    return this.boundingRect.inset(this.selectionRectInset);
  }

  hitTest(worldSpacePoint: Vector, isLoose: boolean): boolean {
    if (isLoose) {
      return this.selectionRect.contains(worldSpacePoint);
    } else {
      return this.hitTestFine(worldSpacePoint);
    }
  }

  hitTestRect(rect: Rect): boolean {
    return this.selectionRect.intersects(rect);
  }

  // eslint-disable-next-line
  hitTestFine(worldSpacePoint: Vector): boolean {
    if (!this.boundingRect.contains(worldSpacePoint)) {
      return false;
    }

    const worldPointRelativeToOrigin = worldSpacePoint
      .clone()
      .subtract(shapeTruePosition2(this.shape));
    const boundingBoxPoint = worldPointRelativeToOrigin.clone();
    const boundingBoxSize = shapeBoundingRect2(this.shape).size;
    const backingScale = new Vector(
      this.canvas.width / boundingBoxSize.x,
      this.canvas.height / boundingBoxSize.y
    );
    const canvasPoint = boundingBoxPoint.clone().multiply(backingScale);
    const ctx = this.canvas.getContext("2d");
    if (ctx) {
      const pointData = ctx.getImageData(
        Math.floor(canvasPoint.x),
        Math.floor(canvasPoint.y),
        1,
        1
      ).data;
      return pointData[3] !== 0;
    }
    return false;
  }

  applyEraser(shape: EraserShape2, id: string, redraw = true) {
    if (!this.boundingRect.intersects(shapeBoundingRect2(shape))) return;
    const shapeCopy = Object.assign({}, shape);
    const myPos = shapeTruePosition2(this.shape);
    const myScale = shapeScale2(this.shape);
    shapeCopy.boundingBoxOrigin = [
      (shape.boundingBoxOrigin[0] - myPos.x) * myScale.x,
      (shape.boundingBoxOrigin[1] - myPos.y) * myScale.y,
    ];

    this.eraserRenderers.push(new PenShapeRenderer(shapeCopy, id));
    if (redraw) this.redraw();
  }

  removeEraser(shape: EraserShape2, id: string, redraw = true) {
    if (!this.boundingRect.intersects(shapeBoundingRect2(shape))) return;
    this.eraserRenderers = this.eraserRenderers.filter(
      (eraserDR) => eraserDR.id !== id
    );
    if (redraw) this.redraw();
  }

  updateTranslate() {
    // Element is positioned in world coordinates using CSS transform
    const origin = this.boundingRect.min;
    const newTransform = `translate3d(${origin.x}px, ${origin.y}px, 0)`;
    if (this.root.style.translate !== newTransform) {
      this.root.style.transform = newTransform;
    }
  }

  prepareCanvasForDrawing(
    backingScale: number,
    canvas: HTMLCanvasElement,
    isInDOM: boolean
  ) {
    // Internal canvas size is as big as the original drawing times the
    // user's screen scale.
    // Only change this if it's different, otherwise some browsers discard
    // the canvas contents.
    const internalWidth = Math.ceil(
      this.shape.boundingBoxOriginalSize[0] * backingScale
    );
    if (canvas.width !== internalWidth) canvas.width = internalWidth;
    const internalHeight = Math.ceil(
      this.shape.boundingBoxOriginalSize[1] * backingScale
    );
    if (canvas.height !== internalHeight) canvas.height = internalHeight;

    if (isInDOM) {
      this.updateTranslate();

      // Element's CSS size is its world space size
      const newWidth = `${Math.ceil(this.boundingRect.size.x)}px`;
      if (canvas.style.width !== newWidth) {
        this.root.style.width = newWidth;
        canvas.style.width = newWidth;
      }
      const newHeight = `${Math.ceil(this.boundingRect.size.y)}px`;
      if (canvas.style.height !== newHeight) {
        this.root.style.height = newHeight;
        canvas.style.height = newHeight;
      }

      const shapeWithAlpha = this.shape as PartialUnknownShape;
      if (shapeWithAlpha.alpha) {
        canvas.style.opacity = `${shapeWithAlpha.alpha * 100}%`;
      }
    }
  }

  applyCanvasTransformToCanvas(ctx: CanvasRenderingContext2D) {
    // Position according to world space
    const origin = this.boundingRect.min;
    ctx.translate(origin.x, origin.y);
    ctx.scale(
      this.shape.boundingBoxSize[0] / this.shape.boundingBoxOriginalSize[0],
      this.shape.boundingBoxSize[1] / this.shape.boundingBoxOriginalSize[1]
    );
  }

  getBackingScale() {
    if (window.devicePixelRatio == null) return 1;
    if (!(window.devicePixelRatio > 1)) return 1;
    return window.devicePixelRatio;
  }

  getDidUpdateRenderedAttributes(
    oldShape: ShapeWithCanvasBasedDOMRepresentation2 | null,
    newShape: ShapeWithCanvasBasedDOMRepresentation2
  ): boolean {
    if (!oldShape) return true;
    // All attributes affect the canvas render except Translate. So check equality of all keys
    // but just skip Translate.
    if (Object.keys(oldShape).length < Object.keys(newShape).length) {
      return true; // a key was added
    }
    for (const k of Object.keys(oldShape)) {
      if (k === AttributeKey.Translate) continue;
      if (
        (oldShape as unknown as Record<string, unknown>)[k] !==
        (newShape as unknown as Record<string, unknown>)[k]
      ) {
        return true;
      }
    }
    return false;
  }

  // eslint-disable-next-line
  update(newShape: Shape2, isLive = true) {
    const oldShape = this.lastRenderedShape;
    this.shape = newShape as ShapeWithCanvasBasedDOMRepresentation2;
    if (isLive) {
      if (this.getDidUpdateRenderedAttributes(oldShape, this.shape)) {
        this.prepareCanvasForDrawing(this.getBackingScale(), this.canvas, true);
        // this.el.dataset.shape = JSON.stringify(newShape);
        // this.el.dataset.shapedata = JSON.stringify(data);
        this.redraw();
      } else {
        this.updateTranslate();
      }
      this.lastRenderedShape =
        newShape as ShapeWithCanvasBasedDOMRepresentation2;
    }
  }

  occlude(viewportRect: Rect) {
    const newValue = this.boundingRect.intersects(viewportRect);
    if (this.isOnscreen && !newValue) {
      this.root.style.display = "none";
      this.isOnscreen = false;
    } else if (!this.isOnscreen && newValue) {
      this.root.style.display = "block";
      this.isOnscreen = true;
    }
  }

  teardown() {
    this.root.parentElement?.removeChild(this.root);
  }

  getShouldClear(): boolean {
    return true;
  }

  redraw() {
    if (!this.ctx) return;
    if (this.getShouldClear()) {
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    }
    this.ctx.save();
    this.ctx.scale(this.getBackingScale(), this.getBackingScale());
    this.drawShape(this.ctx);
    this.drawErasers(this.ctx);
    this.ctx.restore();
  }

  drawShape(ctx: CanvasRenderingContext2D) {
    throw new Error("Unimplemented " + ctx);
  }

  drawErasers(ctx: CanvasRenderingContext2D) {
    ctx.save();
    ctx.globalCompositeOperation = "destination-out";
    for (const r of this.eraserRenderers) {
      this.drawEraser(r, ctx);
    }
    ctx.globalCompositeOperation = "source-over";
    ctx.restore();
  }

  private drawEraser(r: PenShapeRenderer, ctx: CanvasRenderingContext2D) {
    if (!ctx) return;
    if (
      this.shape.eraserBlocklist &&
      this.shape.eraserBlocklist.indexOf(r.id) >= 0
    ) {
      return;
    }
    // debugger;
    ctx.save();
    ctx.lineCap = "round";
    ctx.strokeStyle = "black";
    ctx.lineWidth = r.shape.strokeWidth;

    // Eraser shape's origin has already been moved to be in the local coordinate space
    ctx.translate(r.shape.boundingBoxOrigin[0], r.shape.boundingBoxOrigin[1]);

    const myScale = shapeScale2(this.shape);
    const eraserScale = shapeScale2(r.shape);
    ctx.scale(myScale.x / eraserScale.x, myScale.y / eraserScale.y);
    r.drawShape(ctx);
    ctx.restore();
  }

  renderShapeToCanvas(scaleFactor: number): HTMLCanvasElement {
    const canvas = document.createElement("canvas");
    this.prepareCanvasForDrawing(scaleFactor, canvas, false);
    const ctx = canvas.getContext("2d");
    if (!ctx) return canvas;
    ctx.scale(scaleFactor, scaleFactor);
    this.drawShape(ctx);
    this.drawErasers(ctx);
    return canvas;
  }
}
